import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIsPhone } from '@/hooks/useIsPhone'
import { Button } from '../Button/Button'
// Relative imports (not the '@/components' barrel) to avoid a cycle: the barrel
// exports Workbench, which renders the boxes built on this shell.
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../Sheet/Sheet'
import { Grip, type DragOffset } from './WorkbenchFloatingBits'

/**
 * The shell every "write a sentence about this" box on the chart wears.
 *
 * There are two of them — a trade idea's levels (`WorkbenchIdeaNote`) and a
 * drawing (`WorkbenchDrawingNote`) — and they differ only in their words and in
 * what their buttons ask for. Everything that is FIDDLY is here and exists once:
 * the silent debounced save, the drag, the anti-clip clamp, the click-away
 * commit, and the phone drawer.
 *
 * ## The one exit
 *
 * Every dismissal and every button leaves through `onCommit`, carrying a flag
 * that says what it MEANT. A second callback straight to the caller's "finish"
 * is the shape this flow has repeatedly regressed into: a route that persists
 * the note differently, or not at all, and skips whatever the first route does
 * on the way out. The flag also hands the live text over synchronously, so a
 * press right after typing cannot lose the last 600ms to the debounce.
 *
 * ×, Escape, Enter and a click away commit with NOTHING — they are the same act,
 * "close, keeping what I wrote" — while each button says what it wants next.
 *
 * ## Why there is no Save and no Cancel
 *
 * The note saves itself as it is typed, so a button offering to "save" would
 * describe work it is not doing, and one offering to "cancel" would offer to
 * undo an act the trader never asked for. The honest way to decline is to type
 * nothing and close.
 */

/** Desktop card width. */
const W = 300
/** Typing settles before the note is written, so a keystroke isn't a save. */
const SAVE_DEBOUNCE_MS = 600

/**
 * What a PASSIVE close reports. Both boxes' exit types extend this, so the
 * shell can describe a dismissal without knowing what else that box's buttons
 * can ask for.
 */
export interface NoteBoxClose {
  /** The trader dismissed by pressing outside — its click is still travelling. */
  fromClickAway?: boolean
  /** Pointer position of that dismissing press, viewport coordinates. */
  x?: number
  y?: number
}

export interface NoteBoxAction<E> {
  label: string
  /**
   * Shorter wording for the phone header row, where the button shares its line
   * with a truncating title. "Save trade idea" is written for a full-width
   * button with a row to itself; up there the verb is the part that matters,
   * and the noun is already the subject of the header it sits on.
   */
  phoneLabel?: string
  /** What this button asks the caller for. */
  exit: E
}

export interface WorkbenchNoteBoxProps<E extends NoteBoxClose> {
  /** Names the exact object this box is attached to — never a generic prompt. */
  title: string
  /** The question inside the field. A question, not the field's purpose. */
  placeholder: string
  /** Opening position, relative to the chart wrapper. Desktop only. */
  x: number
  y: number
  initialValue?: string
  /**
   * Focus the field on open. True by default — these boxes come up in response
   * to the trader placing something, and the next thing they do is type. Pass
   * false where the box appears without being asked for, so it cannot steal the
   * keyboard from someone who only meant to select.
   */
  autoFocus?: boolean
  /** The bin, in the header. Destroys the OBJECT, not the note. */
  destructive?: { ariaLabel: string; exit: E } | null
  /** The button that carries the flow. Moves into the header row on a phone. */
  primary?: NoteBoxAction<E> | null
  /** A second choice, always below the field. */
  secondary?: NoteBoxAction<E> | null
  /**
   * Empty data attribute stamped on both shells (e.g. `data-idea-note`), so
   * other surfaces can find or exempt this box.
   */
  markerAttr?: string
  /** Debounced while typing. */
  onSave: (text: string) => void
  /** The ONE exit. Every dismissal and every button comes through here. */
  onCommit: (text: string, exit?: E) => void
}

export function WorkbenchNoteBox<E extends NoteBoxClose>({
  title,
  placeholder,
  x,
  y,
  initialValue = '',
  autoFocus = true,
  destructive = null,
  primary = null,
  secondary = null,
  markerAttr,
  onSave,
  onCommit,
}: WorkbenchNoteBoxProps<E>) {
  const isPhone = useIsPhone()
  const [text, setText] = useState(initialValue)
  // The box owns its position once open, so it is free to be dragged anywhere.
  const [drag, setDrag] = useState<DragOffset>({ dx: 0, dy: 0 })
  const rootRef = useRef<HTMLDivElement | null>(null)
  const areaRef = useRef<HTMLTextAreaElement | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Live mirrors for the document-level click-away listener ONLY. It is
  // registered once, so its closure would otherwise hold whatever was typed at
  // the moment it was bound — and every exit has to hand over the LIVE text.
  // Nothing else reads these: the buttons and the textarea close over the
  // current render's `text`, which is already live.
  const textRef = useRef(initialValue)
  const commitRef = useRef(onCommit)
  useEffect(() => {
    textRef.current = text
    commitRef.current = onCommit
  }, [text, onCommit])

  const commit = (exit?: E) => onCommit(text, exit)

  /**
   * The dismissal routes, guarded so ONE gesture reports ONCE.
   *
   * Only these two can collide: on a phone an outside press or Escape fires the
   * Radix sheet's own `onOpenChange` AND this box's document listener, and a
   * caller that armed the next step twice off one tap is the bug that guard
   * exists to prevent. Buttons and keys are deliberate, separate acts and are
   * deliberately NOT latched — pressing × and then Escape is two closes of a box
   * the caller chose to keep mounted, and the caller decides what that means.
   */
  const dismissedRef = useRef(false)
  const dismissOnce = (exit?: E) => {
    if (dismissedRef.current) return
    dismissedRef.current = true
    commitRef.current(textRef.current, exit)
  }

  useEffect(() => {
    // preventScroll: the box is clamped inside the pane already, and letting
    // focus scroll ancestors shifts the whole workbench layout sideways.
    if (autoFocus) areaRef.current?.focus({ preventScroll: true })
    // Once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Anti-clip: the box varies in height (one footer button or two), so a fixed
  // estimate can't clamp it. Measure the real box after it renders and pull it
  // inside the pane before paint.
  //
  // Where the floor and the bottom edge can't both be honoured, THE FLOOR WINS
  // and the box is allowed to run past the bottom: it is draggable, so a box
  // hanging off the bottom is recoverable, while one behind the strip is not
  // findable at all.
  useLayoutEffect(() => {
    if (isPhone) return
    const el = rootRef.current
    const parent = el?.offsetParent as HTMLElement | null | undefined
    if (!el || !parent) return
    const pad = 8
    // The edit strip is drawn over the chart and outranks nothing here — it is
    // simply in the way. Measured rather than guessed, because the strip moves
    // (it sits lower when the Canvas/Walkthrough pill is up, and the trader can
    // drag it).
    const strip = document.querySelector('[data-idea-strip]')?.getBoundingClientRect()
    const stripFloor = strip ? strip.bottom - parent.getBoundingClientRect().top + pad : 0
    const floor = Math.max(pad, stripFloor)
    const maxX = Math.max(pad, parent.clientWidth - el.offsetWidth - pad)
    const maxY = Math.max(floor, parent.clientHeight - el.offsetHeight - pad)
    setDrag((d) => {
      const nx = Math.min(Math.max(pad, x + d.dx), maxX) - x
      const ny = Math.min(Math.max(floor, y + d.dy), maxY) - y
      return nx === d.dx && ny === d.dy ? d : { dx: nx, dy: ny }
    })
    // Once, on open. Dragging afterwards is the trader's business.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * The field follows its own content on a phone, between the three-line floor
   * and the 40vh cap the classes set. Driven off `scrollHeight` with the height
   * released first, which is the only way to let a field SHRINK again — measure
   * it while it is still tall and you only ever measure the old height.
   *
   * Desktop keeps its fixed three rows and its own resize grip.
   */
  const autoGrow = () => {
    const el = areaRef.current
    if (!isPhone || !el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }
  // On open too: a box reopened on a note written earlier should not deliver two
  // paragraphs through a three-line window.
  useLayoutEffect(autoGrow, [isPhone])

  /**
   * The on-screen keyboard would otherwise cover the field being typed into.
   */
  const [kbInset, setKbInset] = useState(0)
  useEffect(() => {
    if (!isPhone || typeof window === 'undefined' || !window.visualViewport) return
    const vv = window.visualViewport
    const update = () => setKbInset(Math.max(0, window.innerHeight - (vv.height + vv.offsetTop)))
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [isPhone])

  // Silent auto-save: no "Saved!" flash, because a note that saves itself does
  // not need to announce it every few seconds.
  const changeValue = (v: string) => {
    setText(v)
    autoGrow()
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => onSave(v), SAVE_DEBOUNCE_MS)
  }
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
  }, [])

  // Click-away commits, keeping what was typed. Registered a tick late so the
  // trailing click of the gesture that OPENED the box can't immediately close it.
  //
  // It reports HOW and WHERE it closed, and both matter. This fires on
  // pointerdown, so the dismissing gesture is only half over — its `click` is
  // still travelling, and on the chart a click plants a level. The caller can't
  // tell a click-away from an × press without being told, and only the
  // click-away has a trailing click to defend against.
  useEffect(() => {
    const onDocDown = (e: PointerEvent) => {
      const target = e.target as Node | null
      if (rootRef.current?.contains(target)) return
      const el = target instanceof Element ? target : null
      // The phone sheet is portalled to document.body, so it is outside
      // `rootRef` — a press on the box's own field would otherwise close it.
      if (el?.closest('[data-note-sheet]')) return
      // The editing-tools window is the OTHER window of this pair. Picking a
      // colour there must not dismiss the sentence being written about the very
      // drawing being recoloured.
      if (el?.closest('[data-style-toolbar]')) return
      dismissOnce({ fromClickAway: true, x: e.clientX, y: e.clientY } as E)
    }
    const t = setTimeout(() => document.addEventListener('pointerdown', onDocDown, true), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('pointerdown', onDocDown, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headerText = (
    <span className="min-w-0 flex-1 truncate text-caption text-muted-foreground">{title}</span>
  )

  const binButton = destructive ? (
    <button
      type="button"
      // Through the ONE exit, carrying the live text it is about to discard.
      // Handing over '' instead would read as "the note is empty" — and if this
      // ever slipped below the save in the caller, an honest text is the
      // difference between a no-op and a wipe.
      onClick={() => commit(destructive.exit)}
      aria-label={destructive.ariaLabel}
      className={cn(
        'shrink-0 rounded text-muted-foreground hover:text-danger cursor-pointer',
        isPhone ? 'flex h-9 w-9 items-center justify-center' : 'p-0.5',
      )}
    >
      <Trash2 size={isPhone ? 16 : 14} strokeWidth={isPhone ? 1.75 : undefined} />
    </button>
  ) : null

  const closeButton = (
    <button
      type="button"
      onClick={() => commit()}
      aria-label="Close"
      className={cn(
        'shrink-0 rounded text-muted-foreground hover:text-foreground cursor-pointer',
        // 44px reach under a finger; a bare glyph is the easiest thing to miss.
        isPhone ? '-my-2 -mr-2 flex h-11 w-11 items-center justify-center' : 'p-0.5',
      )}
    >
      <X size={isPhone ? 16 : 14} />
    </button>
  )

  const noteField = (
    <textarea
      ref={areaRef}
      rows={3}
      value={text}
      onChange={(e) => changeValue(e.target.value)}
      onKeyDown={(e) => {
        // ENTER COMMITS ON A DESKTOP ONLY. On a phone the return key is how you
        // start a second line, and there is no Shift to hold to get one — so
        // keeping the shortcut there would shut the box on a trader halfway
        // through their first full stop. The phone still has the ×, the footer
        // button and a tap outside.
        if (!isPhone && e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          commit()
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          commit()
        }
        // The chart owns a lot of bare keys; none of them apply while typing.
        e.stopPropagation()
      }}
      placeholder={placeholder}
      // The placeholder holds a QUESTION, not the field's purpose, so it is not
      // a name. The header is.
      aria-label={title}
      className={cn(
        'w-full rounded-md bg-muted text-foreground placeholder:text-muted-foreground outline-none',
        // THREE LINES, THEN IT GROWS. `rows={3}` alone never gives any of it
        // back: one short sentence still pays for three lines, and five get a
        // scrollbar inside a box that has already claimed a quarter of the
        // screen. The cap is a share of the VIEWPORT because the thing being
        // protected is the chart above it, which is measured in screen height.
        isPhone
          ? 'resize-none px-3 py-2 text-body min-h-[4.5rem] max-h-[40vh]'
          : 'resize-y px-2.5 py-1.5 text-label',
      )}
    />
  )

  /**
   * On a phone the flow button moves out of the footer and into the header row,
   * where the × was — the trader presses it on the way through every capture, so
   * it belongs under the thumb rather than below a field that grows.
   *
   * A box with no flow button — which is exactly a drawing's box, since a
   * drawing is not a step in a sequence — keeps its × and this never applies.
   */
  const advanceInHeader = isPhone && !!primary

  const actionButton = (action: NoteBoxAction<E>, variant: 'default' | 'secondary', inHeader: boolean) => (
    <Button
      type="button"
      size="sm"
      variant={variant}
      onClick={() => commit(action.exit)}
      className={cn(
        'font-medium',
        inHeader
          ? // Hugs its word rather than stretching: a full-width button in a
            // header row reads as the header being a button.
            'h-9 shrink-0 px-3 text-caption'
          : cn('w-full', isPhone ? 'h-11 text-label' : 'h-7 text-caption'),
      )}
    >
      <span className="truncate">
        {inHeader ? action.phoneLabel ?? action.label : action.label}
      </span>
    </Button>
  )

  const primaryButton = primary ? actionButton(primary, 'default', advanceInHeader) : null
  // Never rides in the header: two full-width choices cannot share a row, and
  // the flow button is the one that belongs under the thumb.
  const secondaryButton = secondary ? actionButton(secondary, 'secondary', false) : null

  // ── The phone shell ────────────────────────────────────────────────────────
  // No grip: dragging exists so an anchored card can be moved off the thing it
  // covers, and a bottom sheet covers nothing — the handle would be a control
  // with no job, which is worse than no handle.
  //
  // NOT MODAL, and that is the point of the pair. The editing-tools window is
  // still live above this drawer, so a trader can recolour the drawing they are
  // describing without dismissing what they were writing about it.
  if (isPhone) {
    return (
      <Sheet
        open
        modal={false}
        onOpenChange={(o) => {
          if (!o) dismissOnce()
        }}
      >
        <SheetContent
          side="bottom"
          showCloseButton={false}
          showOverlay={false}
          data-note-sheet=""
          {...(markerAttr ? { [markerAttr]: '' } : {})}
          // Radix runs its OWN outside-press dismissal, and it knows nothing
          // about the exemption the document listener above applies — so
          // without this the editing-tools window would close the drawer on a
          // phone even though the same press is harmless on a desktop. Same
          // rule, stated twice because there are two dismissers.
          onInteractOutside={(e) => {
            const t = (e.detail as { originalEvent?: Event }).originalEvent?.target
            if (t instanceof Element && t.closest('[data-style-toolbar]')) e.preventDefault()
          }}
          // The drawer is not a dialog the trader asked to be dropped into: it
          // arrives beside the drawing they selected. Only a just-finished
          // drawing pulls the keyboard up, and that is `autoFocus`'s job —
          // Radix focusing the panel itself would override it.
          onOpenAutoFocus={(e) => {
            if (!autoFocus) e.preventDefault()
          }}
          // The header IS the description — there is no second line to point at,
          // and leaving this unset makes the dialog primitive warn about a
          // description that should not exist.
          aria-describedby={undefined}
          // Lifts the sheet clear of the on-screen keyboard.
          style={{ bottom: kbInset }}
          className="gap-0 border-border bg-popover shadow-bottom-sheet pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          {/* `pt-3` not `pt-4` when a button shares the row: a button reads as
              needing less air above it than a bare glyph does. */}
          <SheetHeader
            className={cn('flex-row items-center gap-2 px-4 pb-2', advanceInHeader ? 'pt-3' : 'pt-4')}
          >
            <SheetTitle className="min-w-0 flex-1 truncate text-label font-medium text-muted-foreground">
              {title}
            </SheetTitle>
            {/* Title · destroy · continue: the safe forward action keeps the
                trailing edge where the thumb lands, and the destructive one is
                the deliberate reach inward. */}
            {binButton}
            {advanceInHeader ? primaryButton : closeButton}
          </SheetHeader>
          <div className="px-4">{noteField}</div>
          {/* Whatever did NOT go up into the header. */}
          {(advanceInHeader ? secondaryButton : primaryButton || secondaryButton) && (
            <div className="flex flex-col gap-2 px-4 pt-3">
              {advanceInHeader ? (
                secondaryButton
              ) : (
                <>
                  {primaryButton}
                  {secondaryButton}
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    )
  }

  // ── The desktop shell ──────────────────────────────────────────────────────
  return (
    <div
      ref={rootRef}
      {...(markerAttr ? { [markerAttr]: '' } : {})}
      className="absolute z-[125] animate-composer-in rounded-md border border-border bg-popover shadow-popover"
      style={{ left: x, top: y, width: W, transform: `translate(${drag.dx}px, ${drag.dy}px)` }}
      // A press inside the box is work, not a dismissal — and must never reach
      // the chart underneath, where it would place a level.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1 px-2 pt-2">
        <Grip drag={drag} setDrag={setDrag} />
        {headerText}
        {/* Left of the ×, which stays the rightmost control: the corner a trader
            reaches for by reflex must be the harmless one. */}
        {binButton}
        {closeButton}
      </div>

      {/* A box with nothing to advance to has no footer, so the field closes the
          card and carries the bottom padding itself. */}
      <div className={cn('mt-2 px-3', !primaryButton && !secondaryButton && 'pb-3')}>{noteField}</div>

      {/* The reading order is question, reasoning, go — which is the order the
          trader acts in. */}
      {(primaryButton || secondaryButton) && (
        <div className="flex flex-col gap-1.5 px-3 pb-3 pt-2">
          {primaryButton}
          {secondaryButton}
        </div>
      )}
    </div>
  )
}
